"use client"

import { useState, useMemo, useRef, useEffect, useCallback, Fragment } from "react"
import { createPortal } from "react-dom"
import { Transition } from "@headlessui/react"
import { Drawer } from "vaul"
import { classNames } from "@/lib/utils"
import { useIsMobile } from "@/hooks/useMediaQuery"

// LocalStorage key for skipped items
export const SKIPPED_ITEMS_KEY = "outfame:setup-checklist:skipped"

// Helper to get skipped items from localStorage
export function getSkippedItems(): Set<string> {
    if (typeof window === "undefined") return new Set()
    try {
        const stored = localStorage.getItem(SKIPPED_ITEMS_KEY)
        return stored ? new Set(JSON.parse(stored)) : new Set()
    } catch {
        return new Set()
    }
}

// Helper to save skipped items to localStorage
export function saveSkippedItems(items: Set<string>) {
    if (typeof window === "undefined") return
    try {
        localStorage.setItem(SKIPPED_ITEMS_KEY, JSON.stringify([...items]))
        // Dispatch storage event for cross-component sync
        window.dispatchEvent(new StorageEvent("storage", { key: SKIPPED_ITEMS_KEY }))
    } catch {
        // Ignore localStorage errors
    }
}

// Items that cannot be skipped (required steps)
// Only "add-targets" can be skipped
const REQUIRED_ITEMS = new Set(["connect-account", "start-growth"])

// Types
export interface SetupChecklistItem {
    id: string
    title: string
    description: string
    actionLabel: string
    actionHref?: string
    onAction?: () => void
    completed: boolean
}

interface SetupChecklistProps {
    items: SetupChecklistItem[]
    className?: string
    /** Callback when "Connect account" action is clicked */
    onConnectAccount?: () => void
    /** Callback when "Upgrade" action is clicked */
    onUpgrade?: () => void
}

// Circular Progress Ring Component (Linktree-style)
function CircularProgress({ percentage, size = 55, strokeWidth = 4.5 }: { percentage: number; size?: number; strokeWidth?: number }) {
    // Use a stable ID to avoid hydration mismatch - only one CircularProgress exists in sidebar
    const gradientId = "sidebar-setup-progress"
    const radius = 25.25 // Exact Linktree radius
    const circumference = 2 * Math.PI * radius // ≈ 158.65
    const strokeDashoffset = circumference - (percentage / 100) * circumference
    const center = size / 2

    return (
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
            <defs>
                <linearGradient id={`progressGradient-${gradientId}`} x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" style={{ stopColor: "#ffa3d1", stopOpacity: 1 }} />
                    <stop offset="100%" style={{ stopColor: "#ff82c0", stopOpacity: 1 }} />
                </linearGradient>
            </defs>
            {/* Background circle */}
            <circle cx={center} cy={center} r={radius} fill="none" stroke="rgba(255, 163, 209, 0.25)" strokeWidth={strokeWidth} />
            {/* Progress circle */}
            <circle
                cx={center}
                cy={center}
                r={radius}
                fill="none"
                stroke={`url(#progressGradient-${gradientId})`}
                strokeWidth={strokeWidth}
                strokeLinecap="round"
                strokeDasharray={circumference}
                strokeDashoffset={strokeDashoffset}
                transform={`rotate(-90 ${center} ${center})`}
                style={{ transition: "stroke-dashoffset 0.5s ease-in-out" }}
            />
            {/* Percentage text */}
            <text x="50%" y="50%" textAnchor="middle" dominantBaseline="central" fontSize={14} fontWeight={600} fill="#ffa3d1" className="font-dashboard">
                {percentage}%
            </text>
        </svg>
    )
}

// ============================================
// SHARED PANEL CONTENT
// ============================================
interface SetupChecklistPanelProps {
    items: SetupChecklistItem[]
    completed: number
    total: number
    percentage: number
    expandedId: string | null
    skippedIds: Set<string>
    onToggle: (id: string) => void
    onSkip: (id: string) => void
    onAction: (item: SetupChecklistItem) => void
    onClose: () => void
}

function SetupChecklistPanel({ items, completed, total, percentage, expandedId, skippedIds, onToggle, onSkip, onAction, onClose }: SetupChecklistPanelProps) {
    return (
        <div className="flex flex-col font-dashboard">
            {/* Header */}
            <div className="flex items-center justify-between p-5 pb-4">
                <div className="flex items-center gap-3">
                    <h2 className="text-lg font-semibold text-gray-900">Your setup checklist</h2>
                    <span className="inline-flex items-center justify-center rounded-full bg-[#ffa3d1] px-2.5 py-0.5 text-xs font-semibold text-white">
                        {completed}/{total}
                    </span>
                </div>
                <button onClick={onClose} className="-m-2 rounded-full p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600" aria-label="Close">
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>
            </div>

            {/* Progress Bar */}
            <div className="px-5 pb-4">
                <div className="h-2 overflow-hidden rounded-full bg-gray-100">
                    <div
                        className="h-full rounded-full transition-all duration-500 ease-out"
                        style={{
                            width: `${percentage}%`,
                            background: "linear-gradient(90deg, #ffa3d1 0%, #ff82c0 100%)"
                        }}
                    />
                </div>
            </div>

            {/* Checklist Items */}
            <div className="max-h-[50vh] overflow-y-auto px-3 pb-5">
                {items.map((item) => (
                    <ChecklistAccordionItem key={item.id} item={item} isExpanded={expandedId === item.id} isSkipped={skippedIds.has(item.id)} onToggle={() => onToggle(item.id)} onSkip={() => onSkip(item.id)} onAction={() => onAction(item)} />
                ))}
            </div>
        </div>
    )
}

// ============================================
// DESKTOP PANEL (Fixed Bottom-Left)
// ============================================
interface DesktopPanelProps {
    isOpen: boolean
    onClose: () => void
    children: React.ReactNode
}

function DesktopPanel({ isOpen, onClose, children }: DesktopPanelProps) {
    const panelRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        if (!isOpen) return

        const handleClickOutside = (e: MouseEvent) => {
            if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
                onClose()
            }
        }

        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === "Escape") onClose()
        }

        document.addEventListener("mousedown", handleClickOutside)
        document.addEventListener("keydown", handleEscape)

        return () => {
            document.removeEventListener("mousedown", handleClickOutside)
            document.removeEventListener("keydown", handleEscape)
        }
    }, [isOpen, onClose])

    if (typeof document === "undefined") return null

    return createPortal(
        <Transition show={isOpen} as={Fragment} enter="transition ease-out duration-300" enterFrom="opacity-0 translate-y-4" enterTo="opacity-100 translate-y-0" leave="transition ease-in duration-200" leaveFrom="opacity-100 translate-y-0" leaveTo="opacity-0 translate-y-4">
            <div ref={panelRef} className="fixed bottom-6 left-3 z-50 w-[400px] origin-bottom-left overflow-hidden rounded-2xl bg-white shadow-xl shadow-gray-900/10 ring-1 ring-gray-900/5">
                {children}
            </div>
        </Transition>,
        document.body
    )
}

// ============================================
// MOBILE DRAWER (Vaul)
// ============================================
interface MobileDrawerProps {
    isOpen: boolean
    onOpenChange: (open: boolean) => void
    children: React.ReactNode
}

function MobileDrawer({ isOpen, onOpenChange, children }: MobileDrawerProps) {
    return (
        <Drawer.Root open={isOpen} onOpenChange={onOpenChange}>
            <Drawer.Portal>
                <Drawer.Overlay className="fixed inset-0 z-50 bg-black/40" />
                <Drawer.Content className="fixed bottom-0 left-0 right-0 z-50 flex max-h-[85vh] flex-col rounded-t-2xl bg-white outline-none">
                    {/* Accessibility: Visually hidden title for screen readers */}
                    <Drawer.Title className="sr-only">Your setup checklist</Drawer.Title>
                    {/* Handle */}
                    <div className="flex justify-center pb-1 pt-3">
                        <div className="h-1 w-8 rounded-full bg-gray-200" />
                    </div>
                    {/* Content */}
                    <div className="pb-safe flex-1 overflow-y-auto">{children}</div>
                </Drawer.Content>
            </Drawer.Portal>
        </Drawer.Root>
    )
}

// ============================================
// MAIN COMPONENT (Sidebar Card)
// ============================================
export default function SetupChecklist({ items, className, onConnectAccount, onUpgrade }: SetupChecklistProps) {
    const [isOpen, setIsOpen] = useState(false)
    const isMobile = useIsMobile()

    // Initialize as empty to avoid hydration mismatch, then load from localStorage
    const [skippedIds, setSkippedIds] = useState<Set<string>>(new Set())
    const [isHydrated, setIsHydrated] = useState(false)

    // Load skipped items from localStorage after hydration
    useEffect(() => {
        setSkippedIds(getSkippedItems())
        setIsHydrated(true)
    }, [])

    // Listen for storage changes from other components (cross-component sync)
    useEffect(() => {
        const handleStorageChange = (e: StorageEvent) => {
            if (e.key === SKIPPED_ITEMS_KEY) {
                setSkippedIds(getSkippedItems())
            }
        }
        window.addEventListener("storage", handleStorageChange)
        return () => window.removeEventListener("storage", handleStorageChange)
    }, [])

    // Find first non-completed, non-skipped item for expansion
    const [expandedId, setExpandedId] = useState<string | null>(null)

    // Set initial expanded item after hydration
    useEffect(() => {
        if (isHydrated) {
            const firstIncomplete = items.find((item) => !item.completed && !skippedIds.has(item.id))
            setExpandedId(firstIncomplete?.id || null)
        }
    }, [isHydrated, items, skippedIds])

    // Update expanded item when panel opens (find first actionable item)
    useEffect(() => {
        if (isOpen && isHydrated) {
            const firstActionable = items.find((item) => !item.completed && !skippedIds.has(item.id))
            if (firstActionable) {
                setExpandedId(firstActionable.id)
            }
        }
    }, [isOpen, isHydrated, items, skippedIds])

    // Progress includes both completed AND skipped items
    const { completed, total, percentage } = useMemo(() => {
        const completedCount = items.filter((item) => item.completed || skippedIds.has(item.id)).length
        const totalCount = items.length
        const pct = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0
        return { completed: completedCount, total: totalCount, percentage: pct }
    }, [items, skippedIds])

    const handleToggle = useCallback((id: string) => {
        setExpandedId((prev) => (prev === id ? null : id))
    }, [])

    const handleSkip = useCallback(
        (id: string) => {
            // Add to skipped items and persist
            setSkippedIds((prev) => {
                const newSkipped = new Set(prev)
                newSkipped.add(id)
                saveSkippedItems(newSkipped)
                return newSkipped
            })
            // Collapse current and expand next non-completed, non-skipped item
            setExpandedId((currentId) => {
                if (currentId === id) {
                    const currentIndex = items.findIndex((item) => item.id === id)
                    const nextItem = items.slice(currentIndex + 1).find((item) => !item.completed && !skippedIds.has(item.id) && item.id !== id)
                    return nextItem?.id || null
                }
                return currentId
            })
        },
        [items, skippedIds]
    )

    const handleAction = useCallback(
        (item: SetupChecklistItem) => {
            // Close the panel first
            setIsOpen(false)

            // Handle specific actions
            if (item.id === "connect-account" && onConnectAccount) {
                onConnectAccount()
            } else if (item.id === "upgrade-plan" && onUpgrade) {
                onUpgrade()
            } else if (item.onAction) {
                item.onAction()
            } else if (item.actionHref) {
                window.location.href = item.actionHref
            }
        },
        [onConnectAccount, onUpgrade]
    )

    const handleClose = useCallback(() => {
        setIsOpen(false)
    }, [])

    const handleCardClick = useCallback(() => {
        setIsOpen(true)
    }, [])

    // Don't show if all items are complete or no items exist
    if (total === 0 || completed === total) {
        return null
    }

    const panelContent = <SetupChecklistPanel items={items} completed={completed} total={total} percentage={percentage} expandedId={expandedId} skippedIds={skippedIds} onToggle={handleToggle} onSkip={handleSkip} onAction={handleAction} onClose={handleClose} />

    return (
        <>
            {/* Sidebar Card - clickable */}
            <div onClick={handleCardClick} className={classNames("flex cursor-pointer flex-col gap-3 rounded-xl bg-white/70 p-4 text-left shadow-sm backdrop-blur-sm transition-all duration-200 hover:bg-white/80 hover:shadow-md", className)}>
                <CircularProgress percentage={percentage} />

                <div className="space-y-0.5">
                    <span className="block text-sm font-semibold text-[#141414]">Your setup checklist</span>
                    <p className="text-xs text-[rgba(20,20,20,0.55)]">
                        {completed} of {total} complete
                    </p>
                </div>

                <button
                    onClick={(e) => {
                        e.stopPropagation()
                        setIsOpen(true)
                    }}
                    className="focus-visible:outline-hidden inline-flex h-10 items-center justify-center gap-2 whitespace-nowrap rounded-xl bg-black px-5 text-sm font-semibold text-white transition-all duration-200 hover:opacity-90 focus-visible:ring-2 focus-visible:ring-black focus-visible:ring-offset-2 active:scale-[0.98] active:opacity-80">
                    Finish setup
                </button>
            </div>

            {/* Responsive Panel/Drawer */}
            {isMobile ? (
                <MobileDrawer isOpen={isOpen} onOpenChange={setIsOpen}>
                    {panelContent}
                </MobileDrawer>
            ) : (
                <DesktopPanel isOpen={isOpen} onClose={handleClose}>
                    {panelContent}
                </DesktopPanel>
            )}
        </>
    )
}

// ============================================
// ACCORDION ITEM
// ============================================
interface ChecklistAccordionItemProps {
    item: SetupChecklistItem
    isExpanded: boolean
    isSkipped: boolean
    onToggle: () => void
    onSkip: () => void
    onAction: () => void
}

function ChecklistAccordionItem({ item, isExpanded, isSkipped, onToggle, onSkip, onAction }: ChecklistAccordionItemProps) {
    // Item is "done" if completed OR skipped
    const isDone = item.completed || isSkipped
    // Item can be expanded/actioned if not done
    const isActionable = !isDone
    // Some items cannot be skipped (required)
    const canSkip = !REQUIRED_ITEMS.has(item.id)

    return (
        <div className={classNames("border-b border-gray-100 last:border-b-0", isDone ? "opacity-60" : "")}>
            {/* Header */}
            <button onClick={onToggle} className="flex w-full items-center gap-3 rounded-lg px-2 py-4 text-left transition-colors hover:bg-gray-50" disabled={isDone}>
                {/* Checkbox Circle - green for completed OR skipped */}
                <div className={classNames("flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2 transition-all duration-200", isDone ? "border-[#3CAA5F] bg-[#3CAA5F]" : "border-gray-300 bg-white")}>
                    {isDone && (
                        <svg className="h-3.5 w-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                    )}
                </div>

                {/* Title - strikethrough for completed OR skipped */}
                <span className={classNames("flex-1 text-sm font-medium", isDone ? "text-gray-400 line-through" : "text-gray-900")}>{item.title}</span>

                {/* Chevron - only show if actionable */}
                {isActionable && (
                    <svg className={classNames("h-5 w-5 text-gray-400 transition-transform duration-200", isExpanded ? "rotate-180" : "")} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                    </svg>
                )}
            </button>

            {/* Expandable Content - only for actionable items */}
            {isActionable && (
                <div className={classNames("overflow-hidden transition-all duration-300 ease-in-out", isExpanded ? "max-h-40 opacity-100" : "max-h-0 opacity-0")}>
                    <div className="px-2 pb-4 pl-11">
                        <p className="mb-3 text-sm text-gray-500">{item.description}</p>
                        <div className="flex items-center gap-3">
                            <button onClick={onAction} className="inline-flex items-center justify-center rounded-full bg-[#3CAA5F] px-4 py-2 text-sm font-semibold text-white transition-all duration-200 hover:bg-[#34965A] active:scale-[0.98]">
                                {item.actionLabel}
                            </button>
                            {/* Only show Skip button if item can be skipped */}
                            {canSkip && (
                                <button onClick={onSkip} className="text-sm font-medium text-gray-500 transition-colors hover:text-gray-700">
                                    Skip
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}
